import cn from 'classnames';
import React from 'react';
import { Icon, IconColor, IconName } from 'web-common/src/shared/design-system/components/Icon';

const GATE_STEPS = [
  { id: 'tastes', label: 'Tastes', iconName: 'check' },
  { id: 'account', label: 'Account', iconName: 'envelope' },
  { id: 'menu', label: 'Menu', iconName: 'dishCloche' },
  { id: 'checkout', label: 'Checkout', iconName: 'cart' },
] as const satisfies readonly { id: string; label: string; iconName: IconName }[];

export type GateStepId = (typeof GATE_STEPS)[number]['id'];

/**
 * At design spacing the bar needs 387px, which overflows a 375pt iPhone SE and a 360dp Android.
 * Tightening below 390 brings it to 341, which fits both. Under 341 it scrolls inside the bar
 * rather than pushing the page sideways.
 */
const TIGHTEN_BELOW_DESIGN_WIDTH = {
  nav: 'max-[389px]:px-2',
  row: 'max-[389px]:gap-0.5',
  separator: 'max-[389px]:w-1',
};

interface Props {
  currentStep: GateStepId;
  className?: string;
}

export const SignUpGateBreadcrumb: React.FC<Props> = ({ currentStep, className }) => (
  <nav
    aria-label='Sign up progress'
    className={cn('no-scrollbar overflow-x-auto bg-eggplant px-4 py-3', TIGHTEN_BELOW_DESIGN_WIDTH.nav, className)}>
    <div className={cn('mx-auto flex w-fit items-center gap-1', TIGHTEN_BELOW_DESIGN_WIDTH.row)}>
      {GATE_STEPS.map(({ id, label, iconName }, index) => (
        <React.Fragment key={id}>
          {index > 0 && (
            <span
              aria-hidden
              className={cn('h-px w-2.5 shrink-0 bg-eggplant-400', TIGHTEN_BELOW_DESIGN_WIDTH.separator)}
            />
          )}
          {/* JA-WEIGHT-SHIM(1218377610766475): font-[600] renders Ja Bold under the shifted map; drop it for text-caption-700-caps when the map lands */}
          <span
            aria-current={id === currentStep ? 'step' : undefined}
            className={cn('flex shrink-0 items-center gap-[3px] text-caption-700-caps font-[600]', {
              'text-milk': id === currentStep,
              'text-eggplant-300': id !== currentStep,
            })}>
            <Icon
              aria-hidden
              name={iconName}
              size={16}
              color={id === currentStep ? IconColor.WHITE : IconColor.EGGPLANT_300}
            />
            <span className='text-box-trim-cap'>{label}</span>
          </span>
        </React.Fragment>
      ))}
    </div>
  </nav>
);
